import { useEffect, useState } from "react";
import { History, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const money = (n) => { const v = Math.abs(Number(n || 0)) < 0.005 ? 0 : Number(n || 0); return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }; // -0.00 never shows
const fmtWeek = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  const mon = new Date(y, m - 1, d);
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  const o = { month: "short", day: "numeric" };
  return `${mon.toLocaleDateString("en-US", o)} – ${sun.toLocaleDateString("en-US", { ...o, year: "numeric" })}`;
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

/** Every pay week that built an employee's banked vacation: hours, accrual,
 *  payout (auto vs. requested) and the running balance. Owner/admin only. */
export default function VacationBankHistoryDialog({ employeeId, employeeName, open, onClose, mine = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || (!employeeId && !mine)) return;
    setBusy(true);
    // `mine`: the signed-in employee's own bank (My Stats) — same numbers payroll sees
    api.get(`/payroll/vacation/${mine ? "mine" : employeeId}/history`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load the vacation history"); setData(null); })
      .finally(() => setBusy(false));
  }, [open, employeeId, mine]);

  const weeks = data ? [...data.weeks].reverse() : []; // newest first
  const totals = data ? data.weeks.filter((w) => w.counts).reduce((t, w) => ({ accrued: t.accrued + w.accrued, paid: t.paid + w.paid_out }), { accrued: 0, paid: 0 }) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#0b0b0f] text-white" data-testid="vac-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-bold uppercase tracking-tight">
            <span className="flex h-8 w-8 items-center justify-center rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"><History className="h-4 w-4" /></span>
            {mine ? "My banked vacation" : `Banked vacation · ${data?.employee_name || employeeName}`}
          </DialogTitle>
        </DialogHeader>

        {busy && <div className="flex items-center gap-2 py-6 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

        {!busy && data && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Banked now" value={money(data.available)} accent testid="vac-history-available" />
              <Stat label={data.baseline ? "Starting balance" : "Manual adjustment"} value={money(data.baseline ? data.baseline.value : data.adjustment)}
                sub={data.baseline ? `set ${fmtWhen(data.baseline.set_at)}${data.baseline.set_by ? ` by ${data.baseline.set_by}` : ""} · from week of ${fmtWeek(data.baseline.week_start).split(" – ")[0]}` : "legacy all-time sum"} testid="vac-history-baseline" />
              <Stat label="Accrued since" value={`+${money(totals.accrued)}`} testid="vac-history-accrued" />
              <Stat label="Paid out since" value={`−${money(totals.paid)}`} testid="vac-history-paid" />
            </div>
            {mine && (
              <p className="text-[11px] leading-snug text-white/60" data-testid="vac-history-mine-note">
                Every saved pay week that built your bank: vacation accrued on your hours, what was paid out, and the balance after. This is exactly what payroll sees.
              </p>
            )}
            {data.opted_out && (
              <p className="rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-200" data-testid="vac-history-waiver">
                Vacation waiver on file — each week's accrual is paid out automatically on that cheque (shown as AUTO), so it never banks.
              </p>
            )}
            {weeks.length === 0 ? (
              <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="vac-history-empty">No saved pay weeks yet</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full text-xs" data-testid="vac-history-table">
                  <thead>
                    <tr className="bg-white/5 font-mono text-[9px] uppercase tracking-widest text-white/50">
                      <th className="px-2 py-1.5 text-left">Pay week</th>
                      <th className="px-2 py-1.5 text-right">Hours</th>
                      <th className="px-2 py-1.5 text-right">Accrued</th>
                      <th className="px-2 py-1.5 text-right">Paid out</th>
                      <th className="px-2 py-1.5 text-right">Balance after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((w) => (
                      <tr key={w.week_start} className={`border-t border-white/5 ${w.counts ? "" : "opacity-40"}`} data-testid={`vac-history-week-${w.week_start}`} title={w.counts ? "" : "Before the starting balance was set — doesn't move the bank"}>
                        <td className="px-2 py-1.5 font-medium">{fmtWeek(w.week_start)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-white/80">{w.hours.toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{w.accrued ? `+${money(w.accrued)}` : "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {w.paid_out ? (
                            <span className="text-amber-300">−{money(w.paid_out)}
                              {w.auto_paid > 0 && <span className="ml-1 text-[9px] uppercase tracking-widest text-sky-300">{w.requested_paid > 0 ? `auto ${money(w.auto_paid)} + req ${money(w.requested_paid)}` : "auto"}</span>}
                              {w.auto_paid <= 0 && w.vacation_hours_paid > 0 && <span className="ml-1 text-[9px] uppercase tracking-widest text-white/50">{w.vacation_hours_paid.toFixed(2)} h</span>}
                            </span>
                          ) : "—"}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-mono font-semibold ${w.balance_after === null ? "text-white/40" : w.balance_after < -0.005 ? "text-red-400" : "text-emerald-200"}`}>
                          {w.balance_after === null ? "n/a" : money(w.balance_after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.bank_sets?.length > 0 && (
              <details className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs" data-testid="vac-history-sets">
                <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-white/60">Starting balance changes ({data.bank_sets.length})</summary>
                <ul className="mt-2 space-y-1 text-white/80">
                  {[...data.bank_sets].reverse().map((s, i) => (
                    <li key={i} className="flex justify-between gap-3"><span>{fmtWhen(s.set_at)}{s.set_by ? ` · ${s.set_by}` : ""} · from week of {fmtWeek(s.week_start).split(" – ")[0]}</span><span className="font-mono">{money(s.value)}</span></li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        <button onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:text-white" data-testid="vac-history-close"><X className="h-4 w-4" /></button>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, sub, accent, testid }) => (
  <div className={`rounded-lg border px-3 py-2 ${accent ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.02]"}`} data-testid={testid}>
    <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">{label}</p>
    <p className={`font-mono text-base font-bold ${accent ? "text-emerald-200" : "text-white"}`}>{value}</p>
    {sub && <p className="mt-0.5 text-[10px] leading-tight text-white/50">{sub}</p>}
  </div>
);
